import {
  ArrowDown,
  ArrowRight,
  Check,
  ChevronDown,
  Coffee,
  LockKeyhole,
  MessageCircle,
} from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { LoyaltyPass } from './LoyaltyPass';
import { BusinessGallery } from './BusinessGallery';
import { ProductFeatures } from './ProductFeatures';
import { AppDownloads, CookiePreferences } from './FooterPreferences';
import { ExampleBrands } from './ProductStories';

export const PLATFORM_URL =
  import.meta.env.VITE_PLATFORM_URL || 'http://localhost:3000/login';

export function Brand() {
  return (
    <a className="brand" href="#produto" aria-label="Fideliza, início">
      fideliza
      <span className="brand-dot" aria-hidden="true" />
    </a>
  );
}

export function PrimaryCta({ className = '' }: { className?: string }) {
  return (
    <a
      className={`button button--primary ${className}`.trim()}
      href={PLATFORM_URL}
    >
      Começar agora <ArrowRight size={17} aria-hidden="true" />
    </a>
  );
}

const showcaseCards = [
  {
    name: 'Café Aurora',
    reward: 'Um café por nossa conta',
    photo: '/images/neighborhood-coffee.avif',
    current: 6,
  },
  {
    name: 'Corte & Companhia',
    variant: 'barber' as const,
    reward: 'Um cuidado a mais',
    background: '#2f4ed3',
    photo: '/images/hero-barber-tools.webp',
    current: 4,
  },
  {
    name: 'flora',
    variant: 'flower' as const,
    reward: 'Um arranjo para você',
    background: '#eed6e7',
    foreground: '#543851',
    current: 5,
  },
  {
    name: 'linho',
    reward: 'Uma peça com desconto',
    background: '#e7e1d1',
    foreground: '#383c2e',
    photo: '/images/business-boutique.avif',
    current: 2,
  },
  {
    name: 'miga',
    reward: 'Um docinho por nossa conta',
    background: '#f0ac80',
    foreground: '#592f25',
    current: 7,
  },
];

export function ConversionHero() {
  const [stack, setStack] = useState(showcaseCards);
  const showNextCard = () =>
    setStack(([first, ...rest]) => [...rest, first]);
  return (
    <div className="conversion-hero">
      <div className="hero-layout page-width">
        <div className="hero-intro">
          <h1>
            Um cartão que fica
            <br className="hero-break--narrow" />
            {' na Wallet '}
            <span className="hero-wallet-icons" aria-hidden="true"><img src="/images/google-wallet-icon.png" alt="" width="512" height="512" decoding="async"/><img src="/images/apple-wallet-icon.png" alt="" width="512" height="512" decoding="async"/></span>
            <br className="hero-break--wide" />
            {'e\u00a0na '}
            <br className="hero-break--narrow" />
            <span className="hero-underline">cabeça do cliente</span>.
          </h1>
          <p className="hero-description">
            Seu cartão de fidelidade na carteira digital do cliente.
            <br /> Sem papel. Sem baixar outro app.
          </p>
          <div className="hero-actions">
            <PrimaryCta />
            <a className="text-link" href="#cartoes">
              Como funciona <ArrowDown size={16} aria-hidden="true" />
            </a>
          </div>
        </div>
        <div
          className="hero-showcase"
          role="group"
          aria-label="Exemplos de cartões para diferentes negócios"
        >
          {stack.map((card, position) => (
            <div
              className="showcase-pass"
              key={card.name}
              style={
                { '--stack-depth': position } as CSSProperties
              }
            >
              <LoyaltyPass {...card} />
            </div>
          ))}
          <button
            className="showcase-cycle"
            type="button"
            onClick={showNextCard}
          >
            Ver o próximo cartão
          </button>
        </div>
      </div>
      <ExampleBrands />
    </div>
  );
}

const joinSteps = [
  {
    title: 'Escaneie o QR',
    description:
      'No balcão, na mesa ou pelo seu link. O primeiro passo já está ao alcance da câmera.',
  },
  {
    title: 'Preencha o cadastro',
    description:
      'O cliente preenche seus dados e entra no programa. Tudo pelo navegador do próprio celular.',
  },
  {
    title: 'Adicione à carteira',
    description:
      'Pronto. O cartão fica na Apple Wallet ou Google Wallet e acompanha cada nova visita.',
  },
];

/** The printed invite that sits on the counter, shown as the object itself. */
function JoinPoster() {
  return (
    <div className="how-poster">
      <span className="how-poster__mark">
        <Coffee size={20} />
      </span>
      <strong>Café Aurora</strong>
      <span>Seu próximo café começa aqui.</span>
      <img src="/images/hero-barcode-qr.svg" alt="" width="52" height="52" loading="lazy" decoding="async" />
      <small>Escaneie. Volte. Aproveite.</small>
      <i className="how-poster__scan" />
    </div>
  );
}

/** The enrollment page the customer lands on, framed only by a browser bar. */
function JoinSignup() {
  return (
    <div className="how-signup">
      <div className="how-signup__bar">
        <LockKeyhole size={10} aria-hidden="true" /> fideliza.app
      </div>
      <div className="how-signup__body">
        <small>CAFÉ AURORA</small>
        <strong>Entre no clube.</strong>
        <span className="how-field">
          Seu nome <b>Marina</b>
        </span>
        <span className="how-field">
          Seu e-mail <b>marina@exemplo.com</b>
        </span>
        <span className="how-signup__submit">Criar meu cartão</span>
      </div>
    </div>
  );
}

const STEP_DURATION = 3000;

/** Each step plays its own scene in turn, and holds its finished state after. */
function usePlayingStep(count: number) {
  const [playing, setPlaying] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const section = sectionRef.current;
    const still =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!section || still || typeof IntersectionObserver !== 'function') return;

    let timer = 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        window.clearInterval(timer);
        // Off screen the loop would burn frames nobody is watching.
        if (!entry.isIntersecting) return;
        timer = window.setInterval(
          () => setPlaying((step) => (step + 1) % count),
          STEP_DURATION,
        );
      },
      { threshold: 0.25 },
    );
    observer.observe(section);
    return () => {
      window.clearInterval(timer);
      observer.disconnect();
    };
  }, [count]);
  return { playing, sectionRef };
}

export function HowItWorksSection() {
  const { playing, sectionRef } = usePlayingStep(joinSteps.length);
  return (
    <section
      className="how-section"
      id="cartoes"
      aria-label="Como funciona"
      data-transition-layer="background"
      ref={sectionRef}
    >
      <div className="how-stage page-width">
        <div className="how-heading">
          <span className="section-kicker">Como funciona</span>
          <h2>
            Do balcão{' '}
            <br />
            para a carteira.
          </h2>
        </div>
        <ol className="how-steps">
          {joinSteps.map((step, index) => (
            <li
              className="how-step"
              key={step.title}
              data-playing={index === playing ? 'true' : undefined}
            >
              <div className="how-step__scene" aria-hidden="true">
                {index === 0 && <JoinPoster />}
                {index === 1 && <JoinSignup />}
                {index === 2 && (
                  <div className="how-wallet">
                    <span className="how-wallet__bar">
                      <Check size={13} aria-hidden="true" /> Adicionado à carteira
                    </span>
                    <LoyaltyPass
                      name="Café Aurora"
                      reward="Seu próximo café por nossa conta"
                      current={1}
                      photo="/images/neighborhood-coffee.avif"
                      label="Cartão do Café Aurora na carteira"
                    />
                  </div>
                )}
              </div>
              <p className="how-step__index">0{index + 1}</p>
              <h3>{step.title}</h3>
              <p className="how-step__text">{step.description}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function ResourcesSection() {
  return (
    <section
      className="resources section-space page-width"
      id="recursos"
      aria-labelledby="resources-title"
    >
      <div className="resources-heading">
        <span className="section-kicker">Muito além do cartão</span>
        <h2 id="resources-title">
          Na carteira do cliente.
          <br />
          <span>Na próxima escolha também.</span>
        </h2>
        <p>
          Um cartão com a sua marca. Uma conversa que continua depois da visita.
        </p>
      </div>
      <ProductFeatures />
    </section>
  );
}

export function BusinessSection() {
  return (
    <section
      className="business-section section-space page-width"
      id="negocios"
      aria-labelledby="business-title"
    >
      <div className="business-heading">
        <span className="section-kicker">Perto de quem volta</span>
        <h2 id="business-title">
          Para todo tipo
          <br /> de negócio.
        </h2>
        <p>
          O café de todo dia. O corte de sempre.
          <br />
          Um bom atendimento merece uma próxima vez.
        </p>
      </div>
      <BusinessGallery />
    </section>
  );
}

const planOptions = [
  {name:'Fideliza Essencial',price:'49',intro:'Para começar uma relação que dura.',features:['Cartões com a sua marca','Apple Wallet e Google Wallet','Clientes e histórico de visitas'],action:'Conhecer o Essencial'},
  {name:'Fideliza Pro',price:'99',intro:'Para fazer o próximo encontro acontecer.',features:['Tudo do Essencial','Recompensas e campanhas','Gestão da equipe e das unidades'],action:'Começar agora'},
  {name:'Fideliza Rede',price:'199',intro:'Para crescer sem perder a proximidade.',features:['Tudo do Pro','Planejamento para múltiplas unidades','Conversa sobre integrações e operação'],action:'Conversar sobre a rede'},
];

export function PlansSection() {
  return (
    <section
      className="plans-section plans-section--three section-space page-width"
      id="planos"
      aria-labelledby="plans-title"
    >
      <div className="plan-intro">
        <span className="section-kicker">Seu próximo passo</span>
        <h2 id="plans-title">
          Um próximo passo.<br />Para cada momento.
        </h2>
        <p>
          Do primeiro cartão a uma rede inteira de boas relações.
        </p>
      </div>
      <p className="pricing-preview-note">Valores ilustrativos para esta prévia. Nomes, benefícios e preços dos planos ainda serão confirmados.</p>
      <div className="plan-options">{planOptions.map((plan,index) => <article key={plan.name} className={`plan-card ${index === 1 ? 'plan-card--featured' : ''}`} aria-labelledby={`plan-name-${index}`}>
        <h3 id={`plan-name-${index}`}>{plan.name}</h3><p className="plan-card__description">{plan.intro}</p>
        <div className="plan-price"><span>R$</span><strong>{plan.price}</strong><small>/mês</small></div>
        <ul>{plan.features.map(feature => <li key={feature}><Check size={17} aria-hidden="true"/>{feature}</li>)}</ul>
        <div className="plan-card__action">{index === 1 ? <PrimaryCta/> : <a className="button button--primary" href={index === 2 ? 'mailto:oi@fideliza.app?subject=Plano%20Rede' : 'mailto:oi@fideliza.app?subject=Plano%20Essencial'}>{plan.action}<ArrowRight size={17}/></a>}</div>
      </article>)}</div>
    </section>
  );
}

const questions = [
  {
    question: 'O cliente precisa baixar um aplicativo?',
    answer:
      'Não. Ele acessa seu link ou QR code, faz o cadastro e adiciona o cartão à Apple Wallet ou à Google Wallet. O cartão fica na carteira digital do próprio celular.',
  },
  {
    question: 'Posso personalizar o cartão com a minha marca?',
    answer:
      'Sim. Você escolhe as cores, adiciona sua marca e define as regras e recompensas do programa. O cartão é do seu negócio, do visual ao benefício.',
  },
  {
    question: 'Como a equipe registra uma visita?',
    answer:
      'A equipe identifica o cliente pelo cartão e registra a visita na plataforma. Você acompanha o histórico de visitas e as recompensas pelo painel.',
  },
  {
    question: 'Funciona para mais de uma unidade?',
    answer:
      'Sim. Você pode organizar as unidades e os membros da equipe no mesmo negócio, com acesso conforme as permissões de cada pessoa.',
  },
  {
    question: 'Como consulto o preço e gerencio a assinatura?',
    answer:
      'O preço é apresentado na plataforma conforme o país de faturamento do seu negócio. Você confere o valor antes de assinar e gerencia a cobrança pelo provedor usado na contratação.',
  },
];

export function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return (
    <section
      className="faq-section section-space page-width"
      id="perguntas"
      aria-labelledby="faq-title"
    >
      <div>
        <span className="section-kicker">Sem pontas soltas</span>
        <h2 id="faq-title">Pode perguntar.</h2>
        <MessageCircle className="faq-heading-icon" size={92} strokeWidth={1.1} aria-hidden="true" />
      </div>
      <div className="faq-list">
        {questions.map(({ question, answer }, index) => (
          <article className="faq-item" key={question}>
            <h3>
              <button
                id={`faq-question-${index}`}
                type="button"
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                {question}
                <ChevronDown size={20} aria-hidden="true" />
              </button>
            </h3>
            <div
              id={`faq-answer-${index}`}
              role="region"
              aria-labelledby={`faq-question-${index}`}
              hidden={openIndex !== index}
            >
              <p>{answer}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function ClosingSection() {
  return (
    <section className="closing-intro" aria-labelledby="closing-title">
      <div className="closing-heading">
        <h2 id="closing-title">
          Até a<br />
          próxima visita.
        </h2>
        <div>
          <p>
            Faça dela o começo
            <br /> de muitas outras.
          </p>
          <PrimaryCta />
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="site-footer" id="comecar">
      <div className="page-width">
        <div className="footer-top">
          <div className="footer-brand">
            <AppDownloads />
          </div>
          <div className="footer-columns">
            <div>
              <strong>Produto</strong>
              <a href="#cartoes">Cartões</a>
              <a href="#recursos">Recursos</a>
              <a href="#planos">Planos</a>
            </div>
            <div>
              <strong>Vamos conversar</strong>
              <a href="mailto:oi@fideliza.app">oi@fideliza.app</a>
              <a href="#perguntas">Perguntas frequentes</a>
              <a href={new URL('/privacidade', PLATFORM_URL).href}>
                Privacidade
              </a>
              <a href={new URL('/termos', PLATFORM_URL).href}>Termos de uso</a>
              <CookiePreferences />
            </div>
            <div>
              <strong>Disponível em</strong>
              <span>Brasil</span>
              <span>Europa</span>
              <span>Estados Unidos</span>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <small>© 2026 Fideliza. Todos os direitos reservados.</small>
          <a href="#produto">
            Voltar ao início <ArrowRight size={15} aria-hidden="true" />
          </a>
        </div>
        <ClosingSection />
        <a
          className="closing-wordmark"
          href="#produto"
          aria-label="Fideliza, início"
        >
          fideliza
          <span aria-hidden="true" />
        </a>
      </div>
    </footer>
  );
}
